import { Controller } from "@hotwired/stimulus"

/**
 * Contrôleur pour gérer l'affichage des champs fiscaux en fonction du pays sélectionné:
 * - Pour les pays UE: afficher VAT et SIRET, masquer DUNS et Sales Tax ID
 * - Pour les pays hors UE: masquer VAT et SIRET, afficher DUNS et Sales Tax ID
 * - Sans pays sélectionné: masquer tous les champs
 * 
 * Les pays de l'UE sont définis dans la constante UE_COUNTRY_CODES
 * 
 * Utilisation :
 * 
 * 1. Ajouter data-controller="eu-vat-fields" au conteneur parent (qui doit contenir aussi le champ hidden country_code)
 * 2. Ajouter data-eu-vat-fields-target="euField" aux champs à afficher pour l'UE (VAT, SIRET)
 * 3. Ajouter data-eu-vat-fields-target="nonEuField" aux champs à afficher pour les pays hors UE (DUNS, Sales Tax ID) 
 * 4. Ajouter data-action="change->eu-vat-fields#updateFieldsVisibility" au champ country_code
 */
export default class extends Controller {
  static targets = ["euField", "nonEuField", "countryCode", "deliveryCountryCode", "copyBillingRadio"]
  static values = { deliveryCountryCode: String }
  
  // Liste des pays de l'UE basée sur le module CountryCode Ruby
  static UE_COUNTRY_CODES = [
    "DE", "AT", "BE", "BG", "CY", "HR", "DK", "ES", "EE", "FI", "FR", "EL", "HU", 
    "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "SE", 
    "CZ", "MC", "SM", "GR"
  ]

  connect() {
    this.updateFieldsVisibility()
  }

  updateFieldsVisibility(event) {
    const countryCode = this.getCountryCode(event)
    
    if (!countryCode) {
      const hasEuData = this.euFieldTargets.some(field => field.value && field.value.trim() !== '')
      const hasNonEuData = this.nonEuFieldTargets.some(field => field.value && field.value.trim() !== '')
      
      if (hasEuData || hasNonEuData) {
        this.toggleFieldsVisibility(this.euFieldTargets, hasEuData)
        this.toggleFieldsVisibility(this.nonEuFieldTargets, hasNonEuData)
      } else {
        this.toggleFieldsVisibility(this.euFieldTargets, false)
        this.toggleFieldsVisibility(this.nonEuFieldTargets, false)
      }
      return
    }
    
    const isEuCountry = this.constructor.UE_COUNTRY_CODES.includes(countryCode)
    
    this.toggleFieldsVisibility(this.nonEuFieldTargets, !isEuCountry)
    this.toggleFieldsVisibility(this.euFieldTargets, isEuCountry)
  }
  
  getCountryCode(event) {
    if (event?.target?.type === 'radio') {
      event = null
    }
    
    if (this.shouldUseDeliveryAddress()) {
      if (this.hasDeliveryCountryCodeValue && this.deliveryCountryCodeValue) {
        return this.deliveryCountryCodeValue.toUpperCase().trim()
      }
      if (this.hasDeliveryCountryCodeTarget) {
        return this.deliveryCountryCodeTarget.value.toUpperCase().trim()
      }
    }
    
    if (event?.target && event.target.value && event.target.value.length === 2) {
      return event.target.value.toUpperCase().trim()
    }
    
    if (this.hasCountryCodeTarget) {
      return this.countryCodeTarget.value.toUpperCase().trim()
    }
    
    return ""
  }
  
  shouldUseDeliveryAddress() {
    const checkedRadio = this.copyBillingRadioTargets.find(radio => radio.checked)
    return checkedRadio?.value === "1"
  }
  
  toggleFieldsVisibility(fields, shouldShow) {
    fields.forEach(field => {
      const container = field.closest('.field-container') || field.parentElement
      
      if (shouldShow) {
        container.classList.remove('hidden')
        field.removeAttribute('disabled')
      } else {
        container.classList.add('hidden')
        field.setAttribute('disabled', 'disabled')
      }
    })
  }
} 